import { RouteErrorBoundary } from '@zuruflex/ui';
import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from './components/AppLayout.js';
import { RequireAuth } from './components/RequireAuth.js';
import { AuditPage } from './pages/AuditPage.js';
import { BookingDetailPage } from './pages/BookingDetailPage.js';
import { BookingsPage } from './pages/BookingsPage.js';
import { ContentEditorPage } from './pages/ContentEditorPage.js';
import { ContentListPage } from './pages/ContentListPage.js';
import { CustomersPage } from './pages/CustomersPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { EmailTemplatesPage } from './pages/EmailTemplatesPage.js';
import { FlagDetailPage } from './pages/FlagDetailPage.js';
import { FlagsPage } from './pages/FlagsPage.js';
import { FleetPage } from './pages/FleetPage.js';
import { InboxPage } from './pages/inbox/InboxPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { PartnerDetailPage } from './pages/PartnerDetailPage.js';
import { PartnersPage } from './pages/PartnersPage.js';
import { PromotionDetailPage } from './pages/PromotionDetailPage.js';
import { PromotionsPage } from './pages/PromotionsPage.js';
import { ProvidersPage } from './pages/ProvidersPage.js';
import { BadgesPage } from './pages/BadgesPage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { ReviewsPage } from './pages/ReviewsPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { VehicleDetailPage } from './pages/VehicleDetailPage.js';

export const router = createBrowserRouter([
  {
    // Same gate, none of the chrome: the editor is a full-page surface.
    element: <RequireAuth />,
    errorElement: <RouteErrorBoundary />,
    children: [{ path: '/content/:slug', element: <ContentEditorPage /> }],
  },
  {
    // Every dashboard route sits behind the gate, including the 404 — an
    // unauthenticated stranger should not learn which paths exist.
    element: <RequireAuth />,
    // Catches a throw from anywhere below, the gate itself included, so a
    // broken component shows this rather than React Router's stack trace.
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/',
        element: <AppLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'fleet', element: <FleetPage /> },
          { path: 'fleet/:idOrSlug', element: <VehicleDetailPage /> },
          { path: 'bookings', element: <BookingsPage /> },
          { path: 'bookings/:reference', element: <BookingDetailPage /> },
          { path: 'inbox', element: <InboxPage /> },
          /*
            The open conversation is the path, not component state, so a
            desk is a link somebody pastes into a handover note. Which
            queue it was found in rides along in the query string.
          */
          { path: 'inbox/:id', element: <InboxPage /> },
          /*
            `/reports` and not `/inbox/reports`: the second collides with
            `inbox/:id` above, and the router would read "reports" as a
            thread id.
          */
          { path: 'reports', element: <ReportsPage /> },
          { path: 'reviews', element: <ReviewsPage /> },
          { path: 'badges', element: <BadgesPage /> },
          { path: 'promotions', element: <PromotionsPage /> },
          { path: 'promotions/:id', element: <PromotionDetailPage /> },
          { path: 'customers', element: <CustomersPage /> },
          { path: 'partners', element: <PartnersPage /> },
          { path: 'partners/:id', element: <PartnerDetailPage /> },
          { path: 'settings', element: <SettingsPage /> },
          /*
            Under settings because it is one: the nav keeps "Settings" lit
            here, and docs/PROVIDERS.md sends people to Settings → Providers.
          */
          { path: 'settings/providers', element: <ProvidersPage /> },
          { path: 'emails', element: <EmailTemplatesPage /> },
          { path: 'emails/:key', element: <EmailTemplatesPage /> },
          /*
            Keys contain dots (`messaging.editMessages`), which the router
            reads as an ordinary segment — links still encode them.
          */
          { path: 'content', element: <ContentListPage /> },
          { path: 'flags', element: <FlagsPage /> },
          { path: 'flags/:key', element: <FlagDetailPage /> },
          { path: 'audit', element: <AuditPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
